// BYOK key priority (plan 102 slice 05b) — the pool's try-order, lower first,
// set by `PATCH /v1/billing/keys/:id/priority` (org, owner/admin) or
// `PATCH /v1/me/keys/:id/priority` (the member's own). AccountKeys and MyKeys
// share this verbatim; only the endpoint and the cache key differ, the
// `createKeyRotate` split again.
//
// THE ONE OPTIMISTIC WRITE ON THE KEYS PAGES, and it still ends in a re-read.
// The card flips to the typed number the moment Save is pressed (a re-rank is
// a reorder the reader is watching), rolls back to the row's prior value if
// the server refuses, and then re-reads the list either way: the patch is a
// preview, the refetch is the answer (`apps/dashboard/CLAUDE.md` § Boundary).
// The rollback is per ROW, not a whole-list snapshot, so a refusal on one card
// cannot undo a concurrent edit on another.
//
// The range check below is a UX hint that spares a round trip; the route's Zod
// schema is the arbiter, and its 400 `detail` is what a refusal toasts.

import { BYOK_KEY_PRIORITY_MAX, BYOK_KEY_PRIORITY_MIN } from '@developerz/domain';
import { type QueryClient, useQueryClient } from '@tanstack/solid-query';
import { t } from '../i18n';
import { createKeyedMutation } from './keyed-mutation';
import { type KeyRow, keysQueryKey, setKeyPriority } from './keys';
import { myKeysQueryKey, setMyKeyPriority } from './my-keys';
import type { ToastTone } from './toast';

/** The route's bounds, from the domain seam that validates them server-side. */
export const KEY_PRIORITY_MIN = BYOK_KEY_PRIORITY_MIN;
export const KEY_PRIORITY_MAX = BYOK_KEY_PRIORITY_MAX;

/** The typed value as a priority, or null when the route would refuse it. */
export function parseKeyPriority(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= KEY_PRIORITY_MIN && n <= KEY_PRIORITY_MAX ? n : null;
}

export type KeyPriorityEdit = {
  /** Awaitable for tests; resolves on failure too (the toast carries it). */
  readonly save: (row: KeyRow, priority: number) => Promise<void>;
  readonly isBusy: (id: string) => boolean;
};

export function createKeyPriorityEdit(input: {
  readonly client: QueryClient;
  /** The list query's key — `keysQueryKey(token)` or `myKeysQueryKey(token)`. */
  readonly queryKey: () => readonly unknown[];
  /** `setKeyPriority` or `setMyKeyPriority`, token pre-bound by the container. */
  readonly set: (id: string, priority: number) => Promise<KeyRow>;
  readonly notify: (message: string, tone?: ToastTone) => void;
}): KeyPriorityEdit {
  const writes = createKeyedMutation({
    fallback: t('account_keys.priority_error'),
    onError: (message) => input.notify(message, 'error'),
  });

  const patch = (key: readonly unknown[], id: string, priority: number): void => {
    input.client.setQueryData<readonly KeyRow[]>(key, (rows) =>
      rows?.map((k) => (k.id === id ? { ...k, priority } : k)),
    );
  };

  const save = async (row: KeyRow, priority: number): Promise<void> => {
    const key = input.queryKey();
    await writes.run(row.id, async () => {
      // An in-flight read landing after the patch would paint the old value.
      await input.client.cancelQueries({ queryKey: key });
      patch(key, row.id, priority);
      try {
        await input.set(row.id, priority);
      } catch (err) {
        patch(key, row.id, row.priority);
        throw err;
      }
      input.notify(t('account_keys.priority_saved_toast'));
    });
    await input.client.refetchQueries({ queryKey: key });
  };

  return { save, isBusy: writes.isBusy };
}

type Notify = (message: string, tone?: ToastTone) => void;

/** The ORG pool's edit (`write:billing`); call in a component body. */
export function useOrgKeyPriority(token: string | null, notify: Notify): KeyPriorityEdit {
  return createKeyPriorityEdit({
    client: useQueryClient(),
    queryKey: () => keysQueryKey(token),
    set: (id, priority) => setKeyPriority(id, priority, token),
    notify,
  });
}

/** The member's PERSONAL pool edit (`write:self:keys`); call in a component body. */
export function useMyKeyPriority(token: string | null, notify: Notify): KeyPriorityEdit {
  return createKeyPriorityEdit({
    client: useQueryClient(),
    queryKey: () => myKeysQueryKey(token),
    set: (id, priority) => setMyKeyPriority(id, priority, token),
    notify,
  });
}
